import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox, Tab } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpDownIcon,
  CheckIcon,
  NewspaperIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { regulatoryIntelligenceApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';
import { PageHeader, StatusBadge, LoadingSpinner } from '../components/ui';
import { useTeamId } from '../hooks/useTeamId';

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const PRIORITY_BADGE: Record<string, { label: string; className: string }> = {
  CRITICAL: { label: 'Critical', className: 'bg-red-100 text-red-700' },
  HIGH: { label: 'High', className: 'bg-orange-100 text-orange-700' },
  MEDIUM: { label: 'Medium', className: 'bg-yellow-100 text-yellow-700' },
  LOW: { label: 'Low', className: 'bg-green-100 text-green-700' },
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  NEW_UPDATE: { label: 'New', className: 'bg-blue-100 text-blue-700' },
  REVIEWED: { label: 'Reviewed', className: 'bg-green-100 text-green-700' },
  ACTION_TAKEN: { label: 'Action Taken', className: 'bg-purple-100 text-purple-700' },
  DISMISSED: { label: 'Dismissed', className: 'bg-gray-100 text-gray-700' },
};

const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'NEW_UPDATE', label: 'New Update' },
  { value: 'REVIEWED', label: 'Reviewed' },
  { value: 'ACTION_TAKEN', label: 'Action Taken' },
  { value: 'DISMISSED', label: 'Dismissed' },
];

const PRIORITY_FILTERS = [
  { value: '', label: 'All Priorities' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const EMPTY_INGEST_FORM = {
  title: '',
  source: '',
  sourceUrl: '',
  publishedDate: '',
  summary: '',
  priority: 'MEDIUM',
  complianceAreas: '',
  affectedProductTypes: '',
  actionItems: '',
  effectiveDate: '',
};

export default function RegulatoryIntelligencePage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [showIngest, setShowIngest] = useState(false);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewActionItems, setReviewActionItems] = useState('');
  const [dismissId, setDismissId] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [viewId, setViewId] = useState<string | null>(null);
  const [ingestForm, setIngestForm] = useState(EMPTY_INGEST_FORM);

  // Team
  const { teamId } = useTeamId();

  // List updates
  const { data: listData, isLoading } = useQuery({
    queryKey: ['regulatoryUpdates', teamId, statusFilter, priorityFilter, agencySearch],
    queryFn: () =>
      regulatoryIntelligenceApi.list(teamId!, {
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        agency: agencySearch || undefined,
      }),
    enabled: !!teamId,
  });
  const updates = listData?.data?.updates || [];
  const total = listData?.data?.total || 0;

  // Dashboard
  const { data: dashData } = useQuery({
    queryKey: ['regulatoryDashboard', teamId],
    queryFn: () => regulatoryIntelligenceApi.getDashboard(teamId!),
    enabled: !!teamId,
  });
  const dashboard = dashData?.data;

  // Trends
  const { data: trendsData } = useQuery({
    queryKey: ['regulatoryTrends', teamId],
    queryFn: () => regulatoryIntelligenceApi.getTrends(teamId!, { months: 12 }),
    enabled: !!teamId,
  });
  const trends = trendsData?.data?.trends || [];

  // View single update
  const { data: viewData } = useQuery({
    queryKey: ['regulatoryUpdate', teamId, viewId],
    queryFn: () => regulatoryIntelligenceApi.get(teamId!, viewId!),
    enabled: !!teamId && !!viewId,
  });
  const viewUpdate = viewData?.data;

  // Ingest mutation
  const ingestMutation = useMutation({
    mutationFn: (data: any) => regulatoryIntelligenceApi.ingest(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulatoryUpdates'] });
      queryClient.invalidateQueries({ queryKey: ['regulatoryDashboard'] });
      toast.success('Regulatory update ingested successfully');
      setShowIngest(false);
      setIngestForm(EMPTY_INGEST_FORM);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to ingest update');
    },
  });

  // Review mutation
  const reviewMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      regulatoryIntelligenceApi.review(teamId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulatoryUpdates'] });
      queryClient.invalidateQueries({ queryKey: ['regulatoryDashboard'] });
      toast.success('Update marked as reviewed');
      setReviewId(null);
      setReviewNotes('');
      setReviewActionItems('');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to review update');
    },
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      regulatoryIntelligenceApi.dismiss(teamId!, id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulatoryUpdates'] });
      queryClient.invalidateQueries({ queryKey: ['regulatoryDashboard'] });
      toast.success('Update dismissed');
      setDismissId(null);
      setDismissReason('');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to dismiss update');
    },
  });

  const handleIngest = () => {
    ingestMutation.mutate({
      title: ingestForm.title,
      source: ingestForm.source,
      sourceUrl: ingestForm.sourceUrl || undefined,
      publishedDate: ingestForm.publishedDate || undefined,
      summary: ingestForm.summary || undefined,
      priority: ingestForm.priority,
      complianceAreas: ingestForm.complianceAreas
        ? ingestForm.complianceAreas.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
      affectedProductTypes: ingestForm.affectedProductTypes
        ? ingestForm.affectedProductTypes.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
      actionItems: ingestForm.actionItems
        ? ingestForm.actionItems.split('\n').map((s) => s.trim()).filter(Boolean)
        : undefined,
      effectiveDate: ingestForm.effectiveDate || undefined,
    });
  };

  const handleReview = () => {
    if (!reviewId) return;
    reviewMutation.mutate({
      id: reviewId,
      data: {
        reviewNotes: reviewNotes || undefined,
        actionItems: reviewActionItems
          ? reviewActionItems.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined,
      },
    });
  };

  const handleDismiss = () => {
    if (!dismissId) return;
    dismissMutation.mutate({ id: dismissId, reason: dismissReason || undefined });
  };

  // Summary counts
  const newCount = updates.filter((u: any) => u.status === 'NEW_UPDATE').length;
  const highPriorityCount = updates.filter((u: any) => u.priority === 'HIGH').length;
  const criticalCount = updates.filter((u: any) => u.priority === 'CRITICAL').length;

  return (
    <FeatureGate feature="regulatory_intelligence" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Regulatory Changes"
          description="Track regulatory updates, review changes, and manage compliance actions"
          actions={
            <button
              onClick={() => setShowIngest(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
            >
              <PlusIcon className="h-4 w-4" />
              Ingest Update
            </button>
          }
        />

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-50 p-2">
                <NewspaperIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Updates</p>
                <p className="text-2xl font-bold text-gray-900">{total}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-yellow-50 p-2">
                <ArrowPathIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">New / Unreviewed</p>
                <p className="text-2xl font-bold text-yellow-600">{newCount}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange-50 p-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">High Priority</p>
                <p className="text-2xl font-bold text-orange-600">{highPriorityCount}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-red-50 p-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Critical</p>
                <p className="text-2xl font-bold text-red-600">{criticalCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tab.Group>
          <Tab.List className="flex gap-4 border-b">
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Updates
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Dashboard
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Trends
            </Tab>
          </Tab.List>

          <Tab.Panels>
            {/* Updates List Tab */}
            <Tab.Panel className="space-y-4 pt-4">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <Listbox value={statusFilter} onChange={setStatusFilter}>
                  <div className="relative w-44">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm">
                      <span>
                        {STATUS_FILTERS.find((f) => f.value === statusFilter)?.label}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                        {STATUS_FILTERS.map((f) => (
                          <Listbox.Option
                            key={f.value}
                            value={f.value}
                            className={({ active }) =>
                              clsx(
                                'cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                active ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                              )
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={clsx('block truncate', selected && 'font-medium')}
                                >
                                  {f.label}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                    <CheckIcon className="h-4 w-4" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>

                <Listbox value={priorityFilter} onChange={setPriorityFilter}>
                  <div className="relative w-44">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm">
                      <span>
                        {PRIORITY_FILTERS.find((f) => f.value === priorityFilter)?.label}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                        {PRIORITY_FILTERS.map((f) => (
                          <Listbox.Option
                            key={f.value}
                            value={f.value}
                            className={({ active }) =>
                              clsx(
                                'cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                active ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                              )
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={clsx('block truncate', selected && 'font-medium')}
                                >
                                  {f.label}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                    <CheckIcon className="h-4 w-4" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>

                <input
                  type="text"
                  placeholder="Search by source/agency..."
                  value={agencySearch}
                  onChange={(e) => setAgencySearch(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {/* Updates table */}
              <div className="rounded-lg border bg-white">
                {isLoading ? (
                  <LoadingSpinner size="md" className="py-12" />
                ) : updates.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <NewspaperIcon className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-lg font-medium">No regulatory updates found</p>
                    <p className="mt-1 text-sm">
                      Ingest your first regulatory update to get started.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Published Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Source
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Priority
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {updates.map((update: any) => {
                          const priorityCfg = PRIORITY_BADGE[update.priority] || PRIORITY_BADGE.LOW;
                          const statusCfg = STATUS_BADGE[update.status] || STATUS_BADGE.NEW_UPDATE;
                          return (
                            <tr key={update.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {update.publishedAt || update.publishedDate
                                  ? new Date(update.publishedAt || update.publishedDate).toLocaleDateString()
                                  : '—'}
                              </td>
                              <td className="max-w-xs truncate px-6 py-4 text-sm font-medium text-gray-900">
                                {update.title}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {update.source}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <StatusBadge variant={
                                  update.priority === 'CRITICAL' ? 'danger' :
                                  update.priority === 'HIGH' ? 'danger' :
                                  update.priority === 'MEDIUM' ? 'warning' :
                                  'success'
                                }>
                                  {priorityCfg.label}
                                </StatusBadge>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <StatusBadge variant={
                                  update.status === 'NEW_UPDATE' ? 'info' :
                                  update.status === 'REVIEWED' ? 'success' :
                                  update.status === 'ACTION_TAKEN' ? 'info' :
                                  'neutral'
                                }>
                                  {statusCfg.label}
                                </StatusBadge>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <div className="flex items-center gap-2">
                                  {update.status === 'NEW_UPDATE' && (
                                    <button
                                      onClick={() => setReviewId(update.id)}
                                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                    >
                                      Review
                                    </button>
                                  )}
                                  {update.status !== 'DISMISSED' && (
                                    <button
                                      onClick={() => setDismissId(update.id)}
                                      className="text-sm font-medium text-gray-500 hover:text-gray-700"
                                    >
                                      Dismiss
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setViewId(update.id)}
                                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                  >
                                    View
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* Dashboard Tab */}
            <Tab.Panel className="space-y-6 pt-4">
              {dashboard ? (
                <>
                  {/* By Agency */}
                  {dashboard.byAgency && dashboard.byAgency.length > 0 && (
                    <div className="rounded-lg border bg-white p-6">
                      <h3 className="text-lg font-semibold text-gray-900">By Agency</h3>
                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Agency
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {dashboard.byAgency.map((item: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.agency || item.source || item.name}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {item.count || item._count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* By Compliance Area */}
                  {dashboard.byComplianceArea && dashboard.byComplianceArea.length > 0 && (
                    <div className="rounded-lg border bg-white p-6">
                      <h3 className="text-lg font-semibold text-gray-900">
                        By Compliance Area
                      </h3>
                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Area
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {dashboard.byComplianceArea.map((item: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.area || item.name}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {item.count || item._count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Recent 5 Updates */}
                  {dashboard.recentUpdates && dashboard.recentUpdates.length > 0 && (
                    <div className="rounded-lg border bg-white p-6">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Recent Updates
                      </h3>
                      <ul className="mt-4 divide-y divide-gray-200">
                        {dashboard.recentUpdates.slice(0, 5).map((item: any) => (
                          <li key={item.id} className="flex items-center justify-between py-3">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {item.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.source} --{' '}
                                {item.publishedAt || item.publishedDate
                                  ? new Date(item.publishedAt || item.publishedDate).toLocaleDateString()
                                  : ''}
                              </p>
                            </div>
                            <StatusBadge
                              variant={
                                item.priority === 'CRITICAL' ? 'danger' :
                                item.priority === 'HIGH' ? 'danger' :
                                item.priority === 'MEDIUM' ? 'warning' :
                                'success'
                              }
                              className="ml-4"
                            >
                              {(PRIORITY_BADGE[item.priority] || PRIORITY_BADGE.LOW).label}
                            </StatusBadge>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <LoadingSpinner size="md" className="py-12" />
              )}
            </Tab.Panel>

            {/* Trends Tab */}
            <Tab.Panel className="space-y-6 pt-4">
              {trends.length > 0 ? (
                <div className="rounded-lg border bg-white p-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Monthly Breakdown
                  </h3>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Month
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Total
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            By Agency
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            By Priority
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {trends.map((t: any, idx: number) => (
                          <tr key={idx}>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                              {t.month || t.period}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                              {t.total || t.count}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              {t.byAgency
                                ? Object.entries(t.byAgency)
                                    .map(([k, v]) => `${k}: ${v}`)
                                    .join(', ')
                                : '--'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              {t.byPriority
                                ? Object.entries(t.byPriority)
                                    .map(([k, v]) => `${k}: ${v}`)
                                    .join(', ')
                                : '--'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  <p className="text-lg font-medium">No trend data available</p>
                  <p className="mt-1 text-sm">
                    Trend data will appear once regulatory updates are tracked over time.
                  </p>
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        {/* Ingest Modal */}
        <Transition appear show={showIngest} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setShowIngest(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Ingest Regulatory Update
                    </Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Title
                        </label>
                        <input
                          type="text"
                          value={ingestForm.title}
                          onChange={(e) =>
                            setIngestForm({ ...ingestForm, title: e.target.value })
                          }
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Source (Agency Name)
                          </label>
                          <input
                            type="text"
                            value={ingestForm.source}
                            onChange={(e) =>
                              setIngestForm({ ...ingestForm, source: e.target.value })
                            }
                            placeholder="e.g., CFPB, OCC, FDIC"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Source URL
                          </label>
                          <input
                            type="text"
                            value={ingestForm.sourceUrl}
                            onChange={(e) =>
                              setIngestForm({ ...ingestForm, sourceUrl: e.target.value })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Published Date
                          </label>
                          <input
                            type="date"
                            value={ingestForm.publishedDate}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                publishedDate: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Priority
                          </label>
                          <select
                            value={ingestForm.priority}
                            onChange={(e) =>
                              setIngestForm({ ...ingestForm, priority: e.target.value })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            {PRIORITY_OPTIONS.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Summary
                        </label>
                        <textarea
                          value={ingestForm.summary}
                          onChange={(e) =>
                            setIngestForm({ ...ingestForm, summary: e.target.value })
                          }
                          rows={3}
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Compliance Areas (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={ingestForm.complianceAreas}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              complianceAreas: e.target.value,
                            })
                          }
                          placeholder="e.g., Fair Lending, BSA/AML, HMDA"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Affected Product Types (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={ingestForm.affectedProductTypes}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              affectedProductTypes: e.target.value,
                            })
                          }
                          placeholder="e.g., Mortgage, Auto Loan, Credit Card"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Action Items (one per line)
                        </label>
                        <textarea
                          value={ingestForm.actionItems}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              actionItems: e.target.value,
                            })
                          }
                          rows={3}
                          placeholder="Enter each action item on a new line"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Effective Date
                        </label>
                        <input
                          type="date"
                          value={ingestForm.effectiveDate}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              effectiveDate: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowIngest(false)}
                        className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleIngest}
                        disabled={
                          !ingestForm.title ||
                          !ingestForm.source ||
                          ingestMutation.isPending
                        }
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {ingestMutation.isPending ? 'Ingesting...' : 'Ingest Update'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Review Modal */}
        <Transition appear show={!!reviewId} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setReviewId(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Review Regulatory Update
                    </Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Review Notes
                        </label>
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          rows={4}
                          placeholder="Enter your review notes..."
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Action Items (one per line)
                        </label>
                        <textarea
                          value={reviewActionItems}
                          onChange={(e) => setReviewActionItems(e.target.value)}
                          rows={3}
                          placeholder="Enter each action item on a new line"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setReviewId(null)}
                        className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleReview}
                        disabled={reviewMutation.isPending}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {reviewMutation.isPending
                          ? 'Reviewing...'
                          : 'Mark as Reviewed'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Dismiss Confirmation */}
        <Transition appear show={!!dismissId} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setDismissId(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Dismiss Update
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-500">
                      Are you sure you want to dismiss this regulatory update? You can
                      optionally provide a reason.
                    </p>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Reason (optional)
                      </label>
                      <textarea
                        value={dismissReason}
                        onChange={(e) => setDismissReason(e.target.value)}
                        rows={3}
                        placeholder="Why is this being dismissed?"
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setDismissId(null)}
                        className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDismiss}
                        disabled={dismissMutation.isPending}
                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {dismissMutation.isPending ? 'Dismissing...' : 'Dismiss'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* View Detail Modal */}
        <Transition appear show={!!viewId} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setViewId(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
                    <div className="flex items-start justify-between">
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Regulatory Update Details
                      </Dialog.Title>
                      <button
                        onClick={() => setViewId(null)}
                        className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    {viewUpdate ? (
                      <div className="mt-4 space-y-4">
                        <div>
                          <h4 className="text-base font-semibold text-gray-900">
                            {viewUpdate.title}
                          </h4>
                          <div className="mt-2 flex items-center gap-2">
                            <span
                              className={clsx(
                                'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                (
                                  PRIORITY_BADGE[viewUpdate.priority] ||
                                  PRIORITY_BADGE.LOW
                                ).className
                              )}
                            >
                              {(
                                PRIORITY_BADGE[viewUpdate.priority] ||
                                PRIORITY_BADGE.LOW
                              ).label}
                            </span>
                            <span
                              className={clsx(
                                'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                (
                                  STATUS_BADGE[viewUpdate.status] ||
                                  STATUS_BADGE.NEW_UPDATE
                                ).className
                              )}
                            >
                              {(
                                STATUS_BADGE[viewUpdate.status] ||
                                STATUS_BADGE.NEW_UPDATE
                              ).label}
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-500">Source</p>
                            <p className="text-gray-900">{viewUpdate.source}</p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-500">Published Date</p>
                            <p className="text-gray-900">
                              {viewUpdate.publishedAt || viewUpdate.publishedDate
                                ? new Date(viewUpdate.publishedAt || viewUpdate.publishedDate).toLocaleDateString()
                                : '—'}
                            </p>
                          </div>
                          {viewUpdate.effectiveDate && (
                            <div>
                              <p className="font-medium text-gray-500">
                                Effective Date
                              </p>
                              <p className="text-gray-900">
                                {new Date(
                                  viewUpdate.effectiveDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          {viewUpdate.sourceUrl && (
                            <div>
                              <p className="font-medium text-gray-500">Source URL</p>
                              <a
                                href={viewUpdate.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View Source
                              </a>
                            </div>
                          )}
                        </div>
                        {viewUpdate.summary && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Summary</p>
                            <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                              {viewUpdate.summary}
                            </p>
                          </div>
                        )}
                        {viewUpdate.complianceAreas &&
                          viewUpdate.complianceAreas.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500">
                                Compliance Areas
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {viewUpdate.complianceAreas.map(
                                  (area: string, i: number) => (
                                    <span
                                      key={i}
                                      className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                                    >
                                      {area}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        {viewUpdate.affectedProductTypes &&
                          viewUpdate.affectedProductTypes.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500">
                                Affected Product Types
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {viewUpdate.affectedProductTypes.map(
                                  (pt: string, i: number) => (
                                    <span
                                      key={i}
                                      className="inline-flex rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                                    >
                                      {pt}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        {viewUpdate.actionItems &&
                          viewUpdate.actionItems.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500">
                                Action Items
                              </p>
                              <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-900">
                                {viewUpdate.actionItems.map(
                                  (item: string, i: number) => (
                                    <li key={i}>{item}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                        {viewUpdate.reviewNotes && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">
                              Review Notes
                            </p>
                            <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                              {viewUpdate.reviewNotes}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                      </div>
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
